'use client';

import type { MutableRefObject } from 'react';
import { useCallback, useRef, useState } from 'react';

export type BrowserUploadValidationFailureReason =
  | 'invalid-type'
  | 'too-large'
  | 'too-many';

export type BrowserUploadRejectedFile = {
  file: File;
  reason: BrowserUploadValidationFailureReason;
};

export type BrowserUploadResult<UploadResult> =
  | {
      status: 'uploaded';
      upload: UploadResult;
    }
  | {
      status: 'failed';
      error: unknown;
    };

const DECLARED_IMAGE_FILE_EXTENSIONS = new Set([
  'avif',
  'bmp',
  'gif',
  'heic',
  'heif',
  'jfif',
  'jpeg',
  'jpg',
  'png',
  'svg',
  'tif',
  'tiff',
  'webp',
]);

function readFileExtension(fileName: string) {
  const normalized = fileName.trim().toLowerCase();
  const dotIndex = normalized.lastIndexOf('.');
  if (dotIndex < 0 || dotIndex === normalized.length - 1) {
    return '';
  }

  return normalized.slice(dotIndex + 1);
}

function matchesAcceptedUploadType(file: File, acceptedType: string) {
  const normalizedAcceptedType = acceptedType.trim().toLowerCase();
  const normalizedFileType = file.type.split(';')[0]?.trim().toLowerCase();

  if (normalizedAcceptedType === 'image/*') {
    return (
      normalizedFileType?.startsWith('image/') === true ||
      DECLARED_IMAGE_FILE_EXTENSIONS.has(readFileExtension(file.name))
    );
  }

  return normalizedFileType === normalizedAcceptedType;
}

export function formatBrowserUploadBytes(bytes: number) {
  const mb = bytes / (1024 * 1024);
  return `${mb.toFixed(mb >= 10 ? 0 : 1)}MB`;
}

export function validateBrowserUploadFiles(params: {
  acceptedTypes?: readonly string[];
  currentCount?: number;
  files: File[];
  isFileTypeAccepted?: (file: File) => boolean;
  maxBytes?: number;
  maxCount?: number;
}) {
  const {
    acceptedTypes,
    currentCount = 0,
    files,
    isFileTypeAccepted,
    maxBytes,
    maxCount,
  } = params;
  const remaining =
    typeof maxCount === 'number'
      ? Math.max(0, maxCount - currentCount)
      : files.length;
  const accepted: File[] = [];
  const rejected: BrowserUploadRejectedFile[] = [];

  for (const file of files) {
    const hasAcceptedType = isFileTypeAccepted
      ? isFileTypeAccepted(file)
      : !acceptedTypes?.length ||
        acceptedTypes.some((acceptedType) =>
          matchesAcceptedUploadType(file, acceptedType)
        );
    if (!hasAcceptedType) {
      rejected.push({
        file,
        reason: 'invalid-type',
      });
      continue;
    }

    if (
      typeof maxBytes === 'number' &&
      Number.isFinite(maxBytes) &&
      maxBytes > 0 &&
      file.size > maxBytes
    ) {
      rejected.push({
        file,
        reason: 'too-large',
      });
      continue;
    }

    if (accepted.length >= remaining) {
      rejected.push({
        file,
        reason: 'too-many',
      });
      continue;
    }

    accepted.push(file);
  }

  return {
    accepted,
    rejected,
  };
}

export function useBrowserFileUploads<
  UploadResult,
  UploadProgress = unknown,
>(params: {
  acceptedTypes?: readonly string[];
  isFileTypeAccepted?: (file: File) => boolean;
  maxBytes?: number;
  maxCount?: number;
  onAllUploadsSettled?: () => void;
  onFirstUploadStart?: () => void;
  pendingUploadCountRef?: MutableRefObject<number>;
  uploadFile: (
    file: File,
    onProgress?: (progress: UploadProgress) => void
  ) => Promise<UploadResult>;
}) {
  const [pendingUploadCount, setPendingUploadCount] = useState(0);
  const internalPendingUploadCountRef = useRef(0);
  const pendingUploadCountRef =
    params.pendingUploadCountRef ?? internalPendingUploadCountRef;

  const validateFiles = useCallback(
    (files: File[], options?: { currentCount?: number }) =>
      validateBrowserUploadFiles({
        acceptedTypes: params.acceptedTypes,
        currentCount: options?.currentCount,
        files,
        isFileTypeAccepted: params.isFileTypeAccepted,
        maxBytes: params.maxBytes,
        maxCount: params.maxCount,
      }),
    [
      params.acceptedTypes,
      params.isFileTypeAccepted,
      params.maxBytes,
      params.maxCount,
    ]
  );

  const beginUpload = useCallback(() => {
    pendingUploadCountRef.current += 1;
    setPendingUploadCount(pendingUploadCountRef.current);

    if (pendingUploadCountRef.current === 1) {
      params.onFirstUploadStart?.();
    }
  }, [params.onFirstUploadStart]);

  const finishUpload = useCallback(() => {
    pendingUploadCountRef.current = Math.max(
      0,
      pendingUploadCountRef.current - 1
    );
    setPendingUploadCount(pendingUploadCountRef.current);

    if (pendingUploadCountRef.current === 0) {
      params.onAllUploadsSettled?.();
    }
  }, [params.onAllUploadsSettled]);

  const uploadAcceptedFile = useCallback(
    async (
      file: File,
      onProgress?: (progress: UploadProgress) => void
    ): Promise<BrowserUploadResult<UploadResult>> => {
      beginUpload();
      try {
        return {
          status: 'uploaded',
          upload: await params.uploadFile(file, onProgress),
        };
      } catch (error) {
        return {
          status: 'failed',
          error,
        };
      } finally {
        finishUpload();
      }
    },
    [beginUpload, finishUpload, params.uploadFile]
  );

  return {
    isUploading: pendingUploadCount > 0,
    pendingUploadCount,
    uploadAcceptedFile,
    validateFiles,
  };
}
